import React from "react";
import {
  Award,
  TrendingUp,
  Users,
  Clock,
  Eye,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

export const PodStandards = () => {
  const performanceMetrics = [
    {
      icon: Award,
      title: "Client Satisfaction",
      value: "≥4.5/5",
      description: "Consistently high client ratings",
      color: "from-green-500 to-green-600",
    },
    {
      icon: TrendingUp,
      title: "Delivery Reliability",
      value: "99.9%",
      description: "On-time sprint completions",
      color: "from-green-500 to-green-600",
    },
    {
      icon: Users,
      title: "Gross Margin",
      value: "≥25%",
      description: "Sustainable value creation",
      color: "from-green-600 to-green-700",
    },
    {
      icon: Clock,
      title: "Utilization Target",
      value: "75%",
      description: "Optimal team productivity",
      color: "from-orange-500 to-red-500",
    },
    {
      icon: TrendingUp,
      title: "Sprint Cycles",
      value: "2 weeks",
      description: "Consistent delivery rhythm",
      color: "from-green-600 to-green-700",
    },
    {
      icon: Eye,
      title: "Transparency",
      value: "24/7",
      description: "Real-time KolaHub visibility",
      color: "from-green-500 to-green-600",
    },
  ];

  const qaLayers = [
    {
      layer: "Peer Review",
      description: "Code reviews and design critiques by team members",
      icon: Users,
      color: "from-green-500 to-green-600",
    },
    {
      layer: "QA Lead Validation",
      description: "Comprehensive testing against requirements and standards",
      icon: CheckCircle,
      color: "from-green-500 to-green-600",
    },
    {
      layer: "Pre-Client Readiness",
      description: "Final quality check and client delivery preparation",
      icon: Award,
      color: "from-green-600 to-green-700",
    },
  ];

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-brand-grey mb-6">
            Performance{" "}
            <span className="text-brand-primary">Standards & Quality</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Measurable commitments we make to every client. These aren't
            aspirations—they're the benchmarks we consistently deliver.
          </p>
        </div>

        {/* Performance Dashboard Grid */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-brand-primary to-brand-tertiary rounded-3xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-8 text-center">
              Pod Performance Standards
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {performanceMetrics.map((metric, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`w-12 h-12 bg-gradient-to-r ${metric.color} rounded-xl flex items-center justify-center`}
                    >
                      <metric.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold mb-2">{metric.value}</div>
                  <div className="text-lg font-semibold mb-1 text-brand-secondary">
                    {metric.title}
                  </div>
                  <div className="text-sm text-white/80">
                    {metric.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 3-Layer QA Process */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-brand-grey text-center mb-4">
            3-Layer Quality Assurance
          </h3>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Every deliverable passes through our rigorous three-layer quality
            process before reaching your hands
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {qaLayers.map((layer, index) => (
              <div key={index} className="relative">
                <div className="bg-white border border-gray-200 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-center">
                  <div
                    className={`w-16 h-16 bg-gradient-to-r ${layer.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}
                  >
                    <layer.icon className="w-8 h-8 text-white" />
                  </div>
                  <div
                    className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${layer.color} text-white text-sm font-bold mb-4`}
                  >
                    Layer {index + 1}
                  </div>
                  <h4 className="text-xl font-bold text-brand-grey mb-3">
                    {layer.layer}
                  </h4>
                  <p className="text-gray-600">{layer.description}</p>
                </div>
                {index < qaLayers.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-brand-primary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-3xl font-bold mb-4 text-brand-primary">
              Experience Pod Excellence
            </h3>
            <p className="text-lg text-gray-600 mb-8">
              See how our standards and quality commitments deliver consistent
              results: higher quality, faster delivery, and better client
              satisfaction.
            </p>
            <button className="group relative overflow-hidden bg-gradient-to-r from-brand-primary to-brand-tertiary text-white px-8 py-4 rounded-2xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <span className="relative z-10 flex items-center justify-center">
                Start Your Pod Journey
                <ArrowRight
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                  size={20}
                />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-brand-tertiary to-brand-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
